import React, { useCallback, useMemo } from "react";
import { useErrorBoundary } from "react-error-boundary";
import { useParams } from "react-router-dom";
import { find, isNil, propEq } from "ramda";

import { AffinityForm, ConfirmationView, FormView } from "affinityForm";
import { translateErrorMessages, useDryRunMutation, useSubmitMutation } from "affinityForm/helpers";
import { Layout } from "affinityUI/structures/layout";
import {
  manualResultsValidationSchema,
  ResultsEditConfirmation,
  ResultsEditForm,
} from "components/views/patientDetails/resultEdit";
import { manualResultValidationErrors } from "components/views/patientDetails/resultEdit/resultsEditForm/messages";
import { ResultFormdata } from "components/views/patientDetails/resultEdit/types";
import { GetPatientResultsQuery, ManualResult, UpdateResult, UpdateResultInput, useGetPatientResultsQuery } from "gql";
import { collectGarbage, composeUpdates, invalidatePatientDetails, invalidatePatientsList } from "gql/invalidations";
import { dateTimeToTimestamp } from "lib/helpers";
import { TestResult } from "types/patient";

const findResult = (id: string) => find<TestResult>(propEq(id, "id"));
const addDigits = (segment: number): string => (segment < 10 ? `0${segment}` : `${segment}`);

const getFormdataByResultId = (id: string, patient: GetPatientResultsQuery["patient"]): Maybe<ResultFormdata> => {
  const getResult = findResult(id);
  const testResults = patient?.testResults;
  if (isNil(testResults)) return null;
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const { belowNeutrophilThreshold, enteredUser, ...result } = getResult(testResults) as ManualResult;
  if (isNil(result)) return null;
  const { dateRecorded } = result;
  const recordedDate = new Date(dateRecorded);
  const timeRecorded = `${addDigits(recordedDate.getHours())}:${addDigits(recordedDate.getMinutes())}`;
  const formData = { ...result, timeRecorded };
  return formData as ResultFormdata;
};

const editFormdataToInput: (data: ResultFormdata) => UpdateResultInput = ({
  dateRecorded,
  timeRecorded,
  id,
  ...restFields
}) => {
  if (isNil(id)) throw new Error("Unknown error");
  return {
    ...restFields,
    id: id as string,
    dateRecorded: dateTimeToTimestamp({ dateRecorded, timeRecorded }),
  };
};

export const EditResultsPage: React.FC = () => {
  const { id, resultId } = useParams<{ id: string; resultId: string }>();
  const { showBoundary: onError } = useErrorBoundary();

  const { loading, data } = useGetPatientResultsQuery({ onError, variables: { id: id as string }, errorPolicy: "all" });
  const patient = data?.patient;
  const testResult = useMemo(() => getFormdataByResultId(resultId as string, patient), [resultId, patient]);
  const update = composeUpdates([invalidatePatientsList, invalidatePatientDetails, collectGarbage]);

  const validateVarSetter = useCallback((formData: ResultFormdata) => {
    return {
      result: editFormdataToInput(formData),
    };
  }, []);

  const validateResult = useDryRunMutation(UpdateResult, {
    varSetter: validateVarSetter,
    dataGetter: useCallback((response) => response?.data?.updateResult, []),
    parseError: translateErrorMessages(manualResultValidationErrors),
  });

  const updateResult = useSubmitMutation(UpdateResult, {
    varSetter: validateVarSetter,
    dataGetter: useCallback((response) => response?.data?.updateResult, []),
    onSave: `/patients/${patient?.id}`,
    update,
  });

  const formTitle = patient ? `Edit result for ${patient.firstName} ${patient.lastName}` : "";
  const confirmationTitle = patient ? `Confirm and Save Result for ${patient.firstName} ${patient.lastName}` : "";

  return !isNil(testResult) ? (
    <AffinityForm<ResultFormdata>
      initialData={testResult}
      asyncValidation={validateResult}
      validationSchema={manualResultsValidationSchema}
      confirmHandler={updateResult}
    >
      <FormView>
        <Layout title={formTitle} loading={loading} showBackBtn>
          {!isNil(patient) && <ResultsEditForm patient={patient} />}
        </Layout>
      </FormView>
      <ConfirmationView>
        <Layout title={confirmationTitle} loading={loading} backLink=".." showBackBtn>
          {!isNil(patient) && <ResultsEditConfirmation patient={patient} />}
        </Layout>
      </ConfirmationView>
    </AffinityForm>
  ) : (
    <></>
  );
};
